import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

/** How often the bell asks for news while the tab is open and visible. */
const POLL_MS = 60_000;

/** "just now", "5 min ago", "3 h ago", "2 d ago", then the date. */
function ago(date) {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} h ago`;
  if (minutes < 7 * 24 * 60) return `${Math.round(minutes / (24 * 60))} d ago`;
  return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * The bell in the dashboard header: gift requests, applications, payouts and
 * the rest, newest first, with a count of the unread ones. Opening one marks
 * it read and goes to the page it is about.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get('/notifications'));
    } catch {
      // The bell is never worth an error on the page; it tries again later.
    }
  }, []);

  // On every page change, and every minute while the tab is in view.
  useEffect(() => {
    load();
  }, [load, pathname]);
  useEffect(() => {
    const timer = setInterval(() => document.visibilityState === 'visible' && load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function markRead(ids) {
    try {
      const { unread } = await api.post('/notifications/read', ids ? { ids } : {});
      setData((d) => ({
        unread,
        notifications: d.notifications.map((n) => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)),
      }));
    } catch {
      // Left unread; the next load shows the truth.
    }
  }

  function openOne(n) {
    setOpen(false);
    if (!n.read) markRead([n.id]);
    if (n.url) navigate(n.url);
  }

  const { notifications, unread } = data;

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md p-1.5 text-ink-muted hover:bg-paper-sunken hover:text-ink"
      >
        <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true">
          <path
            d="M10 2.5a5 5 0 0 0-5 5v2.7c0 .5-.2 1-.5 1.4L3.3 13.3c-.4.6 0 1.4.7 1.4h12c.7 0 1.1-.8.7-1.4l-1.2-1.7c-.3-.4-.5-.9-.5-1.4V7.5a5 5 0 0 0-5-5ZM8 16.5a2 2 0 0 0 4 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close notifications"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="fixed inset-x-2 top-14 z-40 overflow-hidden rounded-lg border border-rule bg-paper-raised shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96">
            <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
              <p className="text-sm font-medium">Notifications</p>
              {unread > 0 && (
                <button type="button" className="text-xs text-accent hover:underline" onClick={() => markRead()}>
                  Mark all as read
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-muted">
                Nothing yet. Gift requests, applications and payouts show up here.
              </p>
            ) : (
              <ul className="max-h-[70vh] divide-y divide-rule overflow-y-auto">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openOne(n)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-paper-sunken"
                    >
                      <span
                        className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read ? 'bg-transparent' : 'bg-accent'}`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm ${n.read ? 'text-ink-muted' : 'font-medium text-ink'}`}>
                          {n.title}
                        </span>
                        <span className="mt-0.5 block text-xs text-ink-faint">{ago(n.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-rule px-4 py-2 text-right">
              <Link to="/dashboard/account" className="text-xs text-ink-muted hover:text-ink">
                Email settings
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
